import PaymentIcon from "@mui/icons-material/Payment";
import { DialogActions, DialogContent, Typography } from "@mui/material";
import {
  Button,
  Checkbox,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  Toolbar,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import DataField from "common/components/Inputs/DataField/DataField";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import usePaymentTransactionForm, {
  UsePaymentTransactionFormProps,
  UsePaymentTransactionFormReturnType,
} from "./usePaymentTransactionForm";

export const PaymentTransactionContext = createContext<
  UsePaymentTransactionFormReturnType | undefined
>(undefined);

export interface PaymentTransactionFormProps extends UsePaymentTransactionFormProps {}

export default function PaymentTransactionForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: PaymentTransactionFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = usePaymentTransactionForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate
    ? I18n.t("PaymentTransaction.addTitle")
    : `#${item?.id} ${item.billing_data?.first_name} ${item.billing_data?.last_name}`;
  const subtitle = isCreate ? undefined : undefined;
  return (
    <ParamsContext.Provider value={{ payment_transaction_id: id }}>
      <PaymentTransactionContext.Provider value={form}>
        <PageHeader
          avatar={<PaymentIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="PaymentTransaction.attributes"
            translationPlaceholdersCategory="PaymentTransaction.placeholders"
            translationHelpersCategory="PaymentTransaction.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("payment_transaction", item)}
          >
            {item?.billing_data && (
              <FieldsContainer>
                <Field fullWidth>
                  <Toolbar>
                    <Typography variant="h6" sx={{ width: "100%" }}>
                      {I18n.t("PaymentTransaction.billingData")}
                    </Typography>

                    <Checkbox id="invoiced" />
                  </Toolbar>
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_first_name"
                    value={item?.billing_data?.first_name || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_last_name"
                    value={item?.billing_data?.last_name || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_email"
                    value={item?.billing_data?.email || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_phone"
                    value={item?.billing_data?.phone || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_is_company"
                    type="boolean"
                    value={item?.billing_data?.is_company || false}
                  />
                </Field>
                {item?.billing_data?.is_company && (
                  <>
                    <Field fullWidth>
                      <DataField
                        id="billing_data_company_name"
                        value={item?.billing_data?.company_name || ""}
                      />
                    </Field>
                    <Field fullWidth>
                      <DataField
                        id="billing_data_vat_number"
                        value={item?.billing_data?.vat_number || ""}
                      />
                    </Field>
                  </>
                )}
                <Field fullWidth>
                  <DataField
                    id="billing_data_country"
                    value={item?.billing_data?.country || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_postcode"
                    value={item?.billing_data?.postcode || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_city"
                    value={item?.billing_data?.city || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_county"
                    value={item?.billing_data?.county || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_street_address"
                    value={item?.billing_data?.street_address || ""}
                  />
                </Field>
                <Field fullWidth>
                  <DataField
                    id="billing_data_apartment"
                    value={item?.billing_data?.apartment || ""}
                  />
                </Field>
              </FieldsContainer>
            )}
            <FieldsContainer>
              <Field fullWidth>
                <DataField id="token" />
              </Field>
              <Field fullWidth>
                <DataField id="booking_id" type="number" />
              </Field>
              <Field fullWidth>
                <DataField id="status" />
              </Field>
              <Field fullWidth>
                <DataField id="gateway" />
              </Field>
              <Field fullWidth>
                <DataField id="amount" type="number" />
              </Field>
              <Field fullWidth>
                <DataField id="currency" />
              </Field>
              <Field fullWidth>
                <DataField id="stripe_checkout_session_id" />
              </Field>
              <Field fullWidth>
                <DataField id="stripe_payment_intent_id" />
              </Field>
              <Field fullWidth>
                <DataField id="expires_at" type="dateTime" />
              </Field>
              <Field fullWidth>
                <DataField id="paid_at" type="dateTime" />
              </Field>
              <Field fullWidth>
                <DataField id="failure_reason" multiline />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="ExchangeRate"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          {hasPermissions([
            "payment_transaction.editany",
            "payment_transaction.editown",
          ]) &&
            canEditItem("payment_transaction", item) && (
              <Button
                onClick={() => save()}
                variant="contained"
                disabled={!isDirty}
                loading={saving}
              >
                {I18n.t("App.save")}
              </Button>
            )}
        </DialogActions>
      </PaymentTransactionContext.Provider>
    </ParamsContext.Provider>
  );
}
